import React, { useState } from 'react';
import {
    Box,
    Stack,
    Button,
    TextField,
    Dropdown,
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHeaderCell,
    TableCell,
    Text,
    Badge,
    Inline,
    Columns,
    Column,
    IconAdd,
    IconEdit,
    IconDelete,
    IconTick,
    Dialog,
    Textarea,
    Drawer,
    IconInfo,
    Actions,
    IconCopy,
} from 'braid-design-system';
import { useToast } from '../hooks/useToast';
import { useTranslation } from 'react-i18next';
import AddMemberForm from '../components/ApproveMobileAppCustomer/AddMemberForm';
import { MobileCustomerProvider, useMobileCustomer } from '../contexts/ApproveMobileCustomerContext';
import Loading from '../components/Loading';
import { useNavigate } from 'react-router-dom';

const ApproveMobileAppCustomerContent: React.FC<{
    onSelect?: (_customerNumber: string) => void;
    hideAddButton?: boolean;
    hideActionColumn?: boolean;
}> = ({ onSelect, hideAddButton = false, hideActionColumn = false }) => {
    const toast = useToast();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [deleteComment, setDeleteComment] = React.useState('');
    const [deactivateOptionId, setDeactivateOptionId] = React.useState<string>('');
    const [customerToDelete, setCustomerToDelete] = React.useState<string | null>(null);

    const {
        customers,
        branches,
        customerTypes,
        deactivateOptions,
        pageLoading,
        loadingMore,
        searchLoading,
        hasMore,
        totalCount,
        searchTerm,
        setSearchTerm,
        selectedBranch,
        setSelectedBranch,
        selectedStatus,
        setSelectedStatus,
        selectedCustomerType,
        setSelectedCustomerType,
        branchLocked,
        handleSearch,
        handleReject,
        refreshCustomers,
        loadMoreCustomers
    } = useMobileCustomer();

    // UI-specific state for drawers
    const [addMemberDrawerOpen, setAddMemberDrawerOpen] = useState(false);
    const [updateMemberDrawerOpen, setUpdateMemberDrawerOpen] = useState(false);
    const [approveDrawerOpen, setApproveDrawerOpen] = useState(false);
    const [deactivationHistoryDrawerOpen, setDeactivationHistoryDrawerOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

    const handleOpenCustomerProfile = (customerNumber: string) => {
        navigate(`/customers/${customerNumber}`);
    };

    const handleDelete = (customerId: string) => {
        setCustomerToDelete(customerId);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deactivateOptionId) {
            toast.showError(t('page.approve_mobile_app_customer.delete.toast.select_option', { defaultValue: 'Please select a deactivation option' }));
            return;
        }
        if (!deleteComment.trim()) {
            toast.showError(t('page.approve_mobile_app_customer.delete.toast.provide_comment', { defaultValue: 'Please provide a comment explaining why this record is being deleted' }));
            return;
        }
        if (!customerToDelete) {
            toast.showError(t('page.approve_mobile_app_customer.delete.toast.no_customer', { defaultValue: 'No customer selected for deletion' }));
            return;
        }

        await handleReject(customerToDelete, { deactivate_id: parseInt(deactivateOptionId), inactive_reason: deleteComment });

        toast.showSuccess(t('page.approve_mobile_app_customer.delete.toast.deleted', { defaultValue: 'Customer {{id}} deleted. Reason: {{reason}}', id: customerToDelete, reason: deleteComment }));
        setDeleteDialogOpen(false);
        setDeleteComment('');
        setDeactivateOptionId('');
        setCustomerToDelete(null);
        await refreshCustomers();
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setDeleteComment('');
        setDeactivateOptionId('');
        setCustomerToDelete(null);
    };

    // Infinite scroll - callback ref for the observer target at the bottom of the table
    const observerRef = React.useRef<IntersectionObserver | null>(null);
    const lastRowRef = React.useCallback(
        (node: HTMLTableRowElement | null) => {
            if (loadingMore) return;
            if (observerRef.current) {
                observerRef.current.disconnect();
            }

            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMore) {
                        loadMoreCustomers();
                    }
                },
                { threshold: 0.1 }
            );

            if (node) {
                observerRef.current.observe(node);
            }
        },
        [hasMore, loadingMore, loadMoreCustomers]
    );

    React.useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, []);

    if (pageLoading) return <Loading />;

    return (
        <Box className="premium-dashboard-bg" style={{ minHeight: '100vh', width: '100%' }}>
            <Box paddingX={["xsmall", "small", "medium", "medium"]} paddingY="medium">
                <Stack space="small">
                    {/* Top Controls */}
                    <Box style={{ background: 'rgba(255, 255, 255, 0.65)', backdropFilter: 'blur(10px)', border: '1px solid rgba(226, 232, 240, 0.8)', padding: '20px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(15, 42, 106, 0.03)' }}>
                        <Columns space="medium" alignY="bottom" collapseBelow="tablet">
                            {!hideAddButton ? (
                                <Column width="content">
                                    <Actions>
                                        <Button
                                            onClick={() => setAddMemberDrawerOpen(true)}
                                            icon={<IconAdd />}
                                            variant="solid"
                                            tone="formAccent"
                                        >
                                            {t('page.approve_mobile_app_customer.button.add_new_member', { defaultValue: 'Add New Member' })}
                                        </Button>
                                    </Actions>
                                </Column>
                            ) : null}
                            <Column>
                                <Box display="flex" justifyContent="flexEnd">
                                    <Inline space="small" alignY="bottom">
                                        <Box width="full">
                                            <form
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    handleSearch();
                                                }}
                                            >
                                                <TextField
                                                    aria-label={t('page.approve_mobile_app_customer.search.aria', { defaultValue: 'Search' })}
                                                    placeholder={t('page.approve_mobile_app_customer.search.placeholder', { defaultValue: 'Search by number, name or NIC...' })}
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.currentTarget.value)}
                                                />
                                            </form>
                                        </Box>
                                        <Box style={{ minWidth: '200px' }}>
                                            <Dropdown
                                                aria-label={t('page.approve_mobile_app_customer.filter.branch_aria', { defaultValue: 'Filter by Branch' })}
                                                value={selectedBranch}
                                                placeholder={t('page.approve_mobile_app_customer.filter.all_branches', { defaultValue: 'All Branches' })}
                                                onChange={(e) => setSelectedBranch(e.currentTarget.value)}
                                                disabled={branchLocked}
                                            >
                                                {[
                                                    ...(!branchLocked ? [<option key="all" value="">{t('page.approve_mobile_app_customer.filter.all_branches', { defaultValue: 'All Branches' })}</option>] : []),
                                                    ...branches.map(branch => (
                                                        <option key={branch.id} value={branch.id.toString()}>
                                                            {branch.name_ln1}
                                                        </option>
                                                    ))
                                                ] as any}
                                            </Dropdown>
                                        </Box>
                                        <Box style={{ minWidth: '150px' }}>
                                            <Dropdown
                                                aria-label={t('page.approve_mobile_app_customer.filter.status_aria', { defaultValue: 'Filter by Status' })}
                                                value={selectedStatus}
                                                placeholder={t('page.approve_mobile_app_customer.filter.all_status', { defaultValue: 'All Status' })}
                                                onChange={(e) => setSelectedStatus(e.currentTarget.value)}
                                            >
                                                <option value="">{t('page.approve_mobile_app_customer.filter.all_status', { defaultValue: 'All Status' })}</option>
                                                <option value="1">{t('page.approve_mobile_app_customer.status.active', { defaultValue: 'Active' })}</option>
                                                <option value="0">{t('page.approve_mobile_app_customer.status.inactive', { defaultValue: 'Inactive' })}</option>
                                                <option value="pending_mobile">{t('page.approve_mobile_app_customer.status.pending_mobile', { defaultValue: 'Pending Mobile App Request' })}</option>
                                            </Dropdown>
                                        </Box>
                                        <Box style={{ minWidth: '200px' }}>
                                            <Dropdown
                                                aria-label={t('page.approve_mobile_app_customer.filter.customer_type_aria', { defaultValue: 'Filter by Customer Type' })}
                                                value={selectedCustomerType}
                                                placeholder={t('page.approve_mobile_app_customer.filter.all_customer_types', { defaultValue: 'All Customer Types' })}
                                                onChange={(e) => setSelectedCustomerType(e.currentTarget.value)}
                                            >
                                                <option value="">{t('page.approve_mobile_app_customer.filter.all_customer_types', { defaultValue: 'All Customer Types' })}</option>
                                                {customerTypes.map(customerType => (
                                                    <option key={customerType.id} value={customerType.id.toString()}>
                                                        {customerType.type_ln1}
                                                    </option>
                                                )) as any}
                                            </Dropdown>
                                        </Box>
                                    </Inline>
                                </Box>
                            </Column>
                        </Columns>
                    </Box>

                    {/* Table */}
                    <Stack space="small">
                        <Box display="flex" justifyContent="spaceBetween" alignItems="center">
                            <Text size="small" tone="secondary">
                                {t('page.approve_mobile_app_customer.summary', { defaultValue: 'Showing {{shown}} of {{total}} customers', shown: customers.length, total: totalCount })}
                            </Text>
                        </Box>
                        {searchLoading && (
                            <Box
                                position="absolute"
                                style={{
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                    zIndex: 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Text weight="strong">{t('page.approve_mobile_app_customer.search.searching', { defaultValue: 'Searching...' })}</Text>
                            </Box>
                        )}
                        <Table label={t('page.approve_mobile_app_customer.table.label', { defaultValue: 'Mobile App Customers' })}>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell><Text>{t('page.approve_mobile_app_customer.table.headers.customer_number', { defaultValue: 'Customer Number' })}</Text></TableHeaderCell>
                                    <TableHeaderCell><Text>{t('page.approve_mobile_app_customer.table.headers.name_nic', { defaultValue: 'Name / NIC No' })}</Text></TableHeaderCell>
                                    <TableHeaderCell><Text>{t('page.approve_mobile_app_customer.table.headers.branch', { defaultValue: 'Branch' })}</Text></TableHeaderCell>
                                    <TableHeaderCell align="center"><Text>{t('page.approve_mobile_app_customer.table.headers.status', { defaultValue: 'Status' })}</Text></TableHeaderCell>
                                    {!hideActionColumn ? (
                                        <TableHeaderCell align="right"><Text>{t('page.approve_mobile_app_customer.table.headers.action', { defaultValue: 'Action' })}</Text></TableHeaderCell>
                                    ) : null}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {customers.length > 0 ? (
                                    <>
                                        {customers.map((customer) => (
                                            <TableRow
                                                key={customer.customer_number}
                                            >
                                                <TableCell>
                                                    <Box
                                                        cursor="pointer"
                                                        onClick={() => {
                                                            handleOpenCustomerProfile(customer.customer_number);
                                                        }}
                                                    >
                                                        <Text tone="info" icon={<IconInfo />} weight="strong">{customer.customer_number}</Text>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack space="xxsmall">
                                                        <Text weight="strong">{customer.name}</Text>
                                                        <Text size="small" tone="secondary">{customer.nic}</Text>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Text>{customer.branch_name}</Text>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Badge tone={customer.status === 'Active' ? 'positive' : 'caution'}>
                                                        {customer.status === "Active" ? t('page.approve_mobile_app_customer.status.active', { defaultValue: 'Active' }) : t('page.approve_mobile_app_customer.status.inactive', { defaultValue: 'Inactive' })}
                                                    </Badge>
                                                </TableCell>
                                                {!hideActionColumn ? (
                                                    <TableCell align="right">
                                                        <Inline space="xsmall" alignY="center" align="right">
                                                            {onSelect && customer.status === 'Active' && (
                                                                <Button
                                                                    size="small"
                                                                    variant="ghost"
                                                                    tone="formAccent"
                                                                    onClick={() => onSelect(customer.customer_number)}
                                                                    aria-label={t('page.approve_mobile_app_customer.action.select_aria', { defaultValue: 'Select customer' })}
                                                                >
                                                                    <IconCopy />
                                                                </Button>
                                                            )}
                                                            {!onSelect && (
                                                                <>
                                                                    {customer.status === 'Inactive' && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="ghost"
                                                                            tone="brandAccent"
                                                                            icon={<IconTick />}
                                                                            onClick={() => {
                                                                                setSelectedCustomer(customer);
                                                                                setApproveDrawerOpen(true);
                                                                            }}
                                                                        >
                                                                            {t('page.approve_mobile_app_customer.action.approve', { defaultValue: 'Approve' })}
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        size="small"
                                                                        variant="ghost"
                                                                        icon={<IconEdit />}
                                                                        onClick={() => {
                                                                            setSelectedCustomer(customer);
                                                                            setUpdateMemberDrawerOpen(true);
                                                                        }}
                                                                    >
                                                                        {t('page.approve_mobile_app_customer.action.update', { defaultValue: 'Update' })}
                                                                    </Button>
                                                                    {customer.status === 'Active' && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="ghost"
                                                                            tone="critical"
                                                                            icon={<IconDelete />}
                                                                            onClick={() => handleDelete(customer.customer_number)}
                                                                        >
                                                                            {t('page.approve_mobile_app_customer.action.deactivate', { defaultValue: 'Deactivate' })}
                                                                        </Button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Inline>
                                                    </TableCell>
                                                ) : null}
                                            </TableRow>
                                        ))}
                                        {hasMore && (
                                            <tr ref={lastRowRef as any}>
                                                <td colSpan={hideActionColumn ? 4 : 5} style={{ height: '1px', padding: 0 }} />
                                            </tr>
                                        )}
                                    </>
                                ) : (
                                    <TableRow>
                                        <TableCell colspan={hideActionColumn ? 4 : 5} align="center">
                                            <Text>{t('page.approve_mobile_app_customer.table.empty', { defaultValue: 'No customers found' })}</Text>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {/* Loading indicator for infinite scroll */}
                        {loadingMore && customers.length > 0 && (
                            <Box display="flex" justifyContent="center" padding="medium">
                                <Text tone="secondary">{t('page.approve_mobile_app_customer.loading.more', { defaultValue: 'Loading more customers...' })}</Text>
                            </Box>
                        )}

                        {!hasMore && customers.length > 0 && customers.length < totalCount && (
                            <Box display="flex" justifyContent="center" padding="medium">
                                <Text tone="secondary" size="small">{t('page.approve_mobile_app_customer.loading.all_loaded', { defaultValue: 'All customers loaded' })}</Text>
                            </Box>
                        )}
                    </Stack>

                    {/* Delete Confirmation Dialog */}
                    <Dialog
                        id="delete-dialog"
                        title={t('page.approve_mobile_app_customer.delete.title', { defaultValue: 'Delete Customer' })}
                        open={deleteDialogOpen}
                        onClose={handleCancelDelete}
                    >
                        <Stack space="large">
                            <Text>
                                {t('page.approve_mobile_app_customer.delete.confirm_text', { defaultValue: 'Are you sure you want to delete this customer record? Please select a deactivation option and provide a comment explaining why.' })}
                            </Text>
                            <Dropdown
                                label={t('page.approve_mobile_app_customer.delete.deactivation_label', { defaultValue: 'Deactivation Option' })}
                                value={deactivateOptionId}
                                onChange={(e) => setDeactivateOptionId(e.currentTarget.value)}
                                placeholder={t('page.approve_mobile_app_customer.delete.deactivation_placeholder', { defaultValue: 'Select a deactivation option' })}
                            >
                                {deactivateOptions.map(option => (
                                    <option key={option.id} value={option.id.toString()}>
                                        {option.name}
                                    </option>
                                ))}
                            </Dropdown>
                            <Textarea
                                label={t('page.approve_mobile_app_customer.delete.comment_label', { defaultValue: 'Comment' })}
                                value={deleteComment}
                                onChange={(e) => setDeleteComment(e.currentTarget.value)}
                                placeholder={t('page.approve_mobile_app_customer.delete.comment_placeholder', { defaultValue: 'Enter reason for deletion...' })}
                            />
                            <Inline space="small">
                                <Button
                                    tone="critical"
                                    onClick={handleConfirmDelete}
                                >
                                    {t('page.approve_mobile_app_customer.delete.confirm_button', { defaultValue: 'Confirm Delete' })}
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={handleCancelDelete}
                                >
                                    {t('page.approve_mobile_app_customer.delete.cancel_button', { defaultValue: 'Cancel' })}
                                </Button>
                            </Inline>
                        </Stack>
                    </Dialog>

                    {/* Deactivation History Drawer */}
                    <Drawer
                        title={t('page.approve_mobile_app_customer.drawer.deactivation_history_title', { defaultValue: 'Deactivation History' })}
                        open={deactivationHistoryDrawerOpen}
                        onClose={() => setDeactivationHistoryDrawerOpen(false)}
                        width="large"
                    >
                        <Stack space="medium">
                            {selectedCustomer ? (
                                <>
                                    <Text weight="strong">
                                        {t('page.approve_mobile_app_customer.drawer.deactivation_history_customer', {
                                            defaultValue: 'Customer {{number}} - {{name}}',
                                            number: selectedCustomer.customer_number,
                                            name: selectedCustomer.name
                                        })}
                                    </Text>
                                    <Text size="small" tone="secondary">
                                        {t('page.approve_mobile_app_customer.drawer.deactivation_history_nic', {
                                            defaultValue: 'NIC: {{nic}}',
                                            nic: selectedCustomer.nic
                                        })}
                                    </Text>
                                    <Text>
                                        {t('page.approve_mobile_app_customer.drawer.deactivation_history_placeholder', {
                                            defaultValue: 'Deactivation history details will be shown here.'
                                        })}
                                    </Text>
                                </>
                            ) : (
                                <Text>
                                    {t('page.approve_mobile_app_customer.drawer.deactivation_history_no_customer', {
                                        defaultValue: 'No customer selected.'
                                    })}
                                </Text>
                            )}
                        </Stack>
                    </Drawer>

                    {/* Add Member Drawer */}
                    <Drawer title={t('page.approve_mobile_app_customer.drawer.add_title', { defaultValue: 'Add New Member' })} open={addMemberDrawerOpen} onClose={() => setAddMemberDrawerOpen(false)} width="large">
                        <AddMemberForm
                            mode="add"
                            onSuccess={() => setAddMemberDrawerOpen(false)}
                            onCancel={() => setAddMemberDrawerOpen(false)}
                        />
                    </Drawer>

                    {/* Approve Member Drawer */}
                    <Drawer title={t('page.approve_mobile_app_customer.drawer.approve_title', { defaultValue: 'Approve Member' })} open={approveDrawerOpen} onClose={() => setApproveDrawerOpen(false)} width="large">
                        {selectedCustomer && (
                            <AddMemberForm
                                mode="approve"
                                customerNumber={selectedCustomer.customer_number}
                                initialData={{ nic: selectedCustomer.nic }}
                                onSuccess={() => setApproveDrawerOpen(false)}
                            />
                        )}
                    </Drawer>

                    {/* Update Member Drawer */}
                    <Drawer title={t('page.approve_mobile_app_customer.drawer.update_title', { defaultValue: 'Update Member' })} open={updateMemberDrawerOpen} onClose={() => setUpdateMemberDrawerOpen(false)} width="large">
                        {selectedCustomer && (
                            <AddMemberForm
                                mode="update"
                                customerNumber={selectedCustomer.customer_number}
                                initialData={{ nic: selectedCustomer.nic }}
                                onSuccess={() => setUpdateMemberDrawerOpen(false)}
                            />
                        )}
                    </Drawer>
                </Stack>
            </Box>
        </Box>
    );
};

const ApproveMobileAppCustomer: React.FC<{
    onSelect?: (_customerNumber: string) => void;
    hideAddButton?: boolean;
    hideActionColumn?: boolean;
}> = ({ onSelect, hideAddButton, hideActionColumn }) => (
    <MobileCustomerProvider>
        <ApproveMobileAppCustomerContent
            onSelect={onSelect}
            hideAddButton={hideAddButton}
            hideActionColumn={hideActionColumn}
        />
    </MobileCustomerProvider>
);

export default ApproveMobileAppCustomer;
